import React from "react";
import { ChevronRight } from "lucide-react";
import RecentTransactions from "./RecentTransactions";
import ActivityFeed from "./ActivityFeed";
import { safeCurrencyFormat } from "../../utilities/safeCurrencyFormat";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const normalizeStatus = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "success" || v === "completed" || v === "complete") return "Completed";
    if (v === "pending" || v === "processing" || v === "in_progress" || v === "in-progress")
        return "Processing";
    if (v === "reported") return "Reported";
    if (
        v === "failed" ||
        v === "declined" ||
        v === "rejected" ||
        v === "canceled" ||
        v === "cancelled" ||
        v === "error"
    )
        return "Failed";
    return s || "—";
};

const statusClass = (s) => {
    const n = normalizeStatus(s);
    if (n === "Completed") return "text-[#16A34A]";
    if (n === "Processing") return "text-orange-500";
    if (n === "Reported") return "text-sky-500";
    if (n === "Failed") return "text-red-500";
    return "text-[#6F739B]";
};

export default function DashboardActivitySection({
    className = "",
    transactions = [],
    onViewAllTransactions,
    feed = [],
}) {
    const navigate = useNavigate();
    const { business } = useSelector((s) => s);
    const [feedTypes, setFeedTypes] = React.useState([]);

    const filteredFeed = React.useMemo(() => {
        if (!feedTypes?.length) return feed;
        const allow = new Set(feedTypes);
        return feed.filter((it) => allow.has(it.type));
    }, [feed, feedTypes]);

    const groupedTx = groupByCalendarDay(transactions);
    // const groupedFeed = groupByCalendarDay(filteredFeed); come back here later when feeds are properly filtered
    const groupedFeed = React.useMemo(() => [{ label: null, items: filteredFeed }], [filteredFeed]); // temporary fix to feed

    const filterSummary = feedTypes.length ? `${feedTypes.length} selected` : "All";

    const handleFeedCta = ({ id, action, type, raw, metadata }) => {
        const isViewy = action === "view" || action === "reminder" || action === "review";
        if (!isViewy) return;

        if (type === "invoice") {
            const invoiceId = raw?._id || id;
            if (invoiceId) navigate(`/invoice/manage/${invoiceId}`);
            return;
        }

        if (type === "po") {
            const orderId = raw?._id || id;
            let poType;
            const cat = (metadata?.category || "").toLowerCase();
            if (cat.includes("outbound")) poType = "outbound";
            if (cat.includes("inbound")) poType = "inbound";
            const bizId = business?._id;
            if (!poType && bizId) {
                if (raw?.inbound?.businessId === bizId) poType = "inbound";
                else if (raw?.outbound?.businessId === bizId) poType = "outbound";
            }
            navigate(`/flex/home/manage-po?orderId=${orderId}&type=${poType || "inbound"}`);
            return;
        }
    };

    return (
        <section className={`grid grid-cols-1 lg:grid-cols-12 gap-4 ${className}`}>
            {/* Recent transactions */}
            <div className="lg:col-span-6 rounded-2xl bg-white p-4 md:p-6">
                <RecentTransactions
                    groupedTx={groupedTx}
                    Header={Header}
                    DayBlock={DayBlock}
                    Divider={Divider}
                    TxRow={TxRow}
                    EmptyState={EmptyState}
                    onViewAll={onViewAllTransactions}
                />
            </div>

            {/* Feed */}
            <div className="lg:col-span-6 rounded-2xl bg-white p-4 md:p-6">
                <ActivityFeed
                    blocks={groupedFeed}
                    count={filteredFeed.length}
                    selectedTypes={feedTypes}
                    filterSummary={filterSummary}
                    onChangeFilter={setFeedTypes}
                    Divider={Divider}
                    FeedRow={FeedRow}
                    EmptyState={EmptyState}
                    onCta={handleFeedCta}
                />
            </div>
        </section>
    );
}

function Header({ title, actionLabel, onAction }) {
    return (
        <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-[#101828]">{title}</h3>
            {actionLabel && (
                <button
                    onClick={onAction}
                    className="inline-flex items-center gap-1 text-sm text-[#101828] px-2 py-1 rounded-lg hover:bg-[#F8F9FB]"
                >
                    {actionLabel}
                    <ChevronRight className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}

function DayBlock({ label, children }) {
    const rows = React.Children.toArray(children).filter(Boolean);

    return (
        <div>
            <div className="flex items-center gap-3">
                <div className="text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]">
                    {label}
                </div>
                <div className="h-px bg-[#EEF0F6] flex-1" />
            </div>
            <div className="mt-1 divide-y divide-[#EEF0F6]">
                {rows.map((row, i) => (
                    <div key={i} className="py-2">
                        {row}
                    </div>
                ))}
            </div>
        </div>
    );
}

function Divider() {
    return <div className="h-px bg-[#EEF0F6] my-3" />;
}

function CircleIcon({ children }) {
    return (
        <div className="h-10 w-10 rounded-full bg-[#F2F3FC] grid place-items-center text-[#6F00FF]">
            {children || <span className="h-2 w-2 rounded-full bg-current" />}
        </div>
    );
}

function MetaDot() {
    return <span className="mx-2 inline-block h-1 w-1 rounded-full bg-[#D1D5DB]" />;
}

function TxRow({
    title,
    timeISO,
    status = "Completed",
    amount = 0,
    direction = "out",
    icon,
    currency,
}) {
    const isCredit = direction === "in" || Number(amount) > 0;
    const isCompleted = String(status).toLowerCase() === "completed";
    const isCrypto =
        String(currency).toUpperCase() === "USDT" || String(currency).toUpperCase() === "USDC";
    const formattedAmount = isCrypto
        ? `${Number(Math.abs(amount)).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          })} ${String(currency).toUpperCase()}`
        : safeCurrencyFormat(Math.abs(amount), currency);

    const displayStatus = normalizeStatus(status);

    return (
        <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 min-w-0">
                <CircleIcon>{icon}</CircleIcon>
                <div className="min-w-0">
                    <div
                        className="text-[16px] font-[450] text-[#161827] truncate max-w-[220px] sm:max-w-[280px]"
                        title={title}
                    >
                        {title}
                    </div>
                    <div className="mt-1 text-[14px] font-[350] text-[#6F739B]">
                        {formatTime(timeISO)}
                        <MetaDot />
                        <span className={`${statusClass(displayStatus)} font-medium`}>
                            {displayStatus}
                        </span>
                    </div>
                </div>
            </div>

            <div
                className={`text-sm md:text-base font-semibold ${
                    isCredit && isCompleted ? "text-[#16A34A]" : "text-[#101828]"
                }`}
            >
                {isCredit ? "" : "-"}
                {formattedAmount}
            </div>
        </div>
    );
}

function FeedRow({ id, message, metadata, action, type, raw, onCta }) {
    const ctaText = ctaTextFor({ action, type, raw });

    const subtitleLeft = prettyType(type);
    const subtitleRight = metadata?.business || metadata?.category || "";

    return (
        <div className="flex items-center justify-between">
            <div className="flex min-w-0">
                <div className="min-w-0">
                    <div
                        className="text-[16px] text-[#161827] font-[400] truncate max-w-[260px]"
                        title={message}
                    >
                        {renderHighlights(message)}
                    </div>
                    {(subtitleLeft || subtitleRight) && (
                        <div className="mt-1 text-[14px] text-[#383B56] font-[350] truncate">
                            {subtitleLeft}
                            {subtitleLeft && subtitleRight ? " • " : ""}
                            {subtitleRight}
                        </div>
                    )}
                </div>
            </div>

            {ctaText && (
                <button
                    onClick={() => onCta?.({ id, action, type, raw, metadata })}
                    className="shrink-0 rounded-[8px] border border-solid shadow-[0_0_3px_rgba(146,149,180,0.25)] font-[450] border-[#F2F3FC] px-3 py-2 text-[14px] text-[#383B56] hover:bg-[#F8F9FB]"
                >
                    {ctaText}
                </button>
            )}
        </div>
    );
}

function groupByCalendarDay(items) {
    if (!Array.isArray(items)) return [];
    const map = new Map();
    items.forEach((it) => {
        const iso = it?.timeISO || it?.date || "";
        const key = iso ? iso.slice(0, 10) : "unknown";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({ ...it, timeISO: iso || it?.timeISO });
    });

    const ordered = [...map.entries()].sort(([a], [b]) => (a < b ? 1 : -1));
    return ordered.map(([key, arr]) => ({
        label: humanDayLabel(key),
        items: arr,
    }));
}

function humanDayLabel(yyyy_mm_dd) {
    const d = new Date(yyyy_mm_dd);
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);

    if (sameDay(d, today)) return "Today";
    if (sameDay(d, yest)) return "Yesterday";

    const opts = { weekday: "short", day: "2-digit", month: "short" };
    return d.toLocaleDateString(undefined, opts).toUpperCase();
}

function sameDay(a, b) {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

function formatTime(iso) {
    try {
        const d = iso ? new Date(iso) : new Date();
        return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
}

function EmptyState({ label, className = "" }) {
    return (
        <div
            className={`flex items-center justify-center py-10 text-sm text-[#6B7280] ${className}`}
        >
            {label}
        </div>
    );
}

function prettyType(type) {
    if (!type) return "";
    const map = {
        invoice: "Invoice",
        po: "Purchase Order",
        shipment: "Trucking",
        quote: "Quote",
        loan: "Working Capital",
    };
    return map[type] || type[0].toUpperCase() + type.slice(1);
}

function ctaTextFor({ action, type, raw }) {
    if (action === "reminder") return "Make payment";
    if (action === "review") {
        if (type === "shipment") return "Review shipment";
        if (type === "quote") return "Review quote";
        if (type === "invoice" || type === "po") return "Review";
        return "Review";
    }
    if (action === "view") {
        if (type === "po") return "View order";
        if (type === "invoice") return "View invoice";
        return "View";
    }
    return null;
}

function renderHighlights(text) {
    if (!text) return null;

    const parts = [];
    const patterns = [
        /(₦\s?\d{1,3}(?:,\d{3})*(?:\.\d+)?)|\bNGN\s?\d{1,3}(?:,\d{3})*(?:\.\d+)?\b/gi,
        /\b(?:TIN|TPO|INV|PO)\s*[-–]?\s*\d+\b/g,
    ];

    let last = 0;
    const regex = new RegExp(`${patterns.map((r) => r.source).join("|")}`, "gi");
    let m;
    while ((m = regex.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        parts.push(<strong key={m.index}>{m[0]}</strong>);
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return <>{parts}</>;
}
